import { useQueryClient } from '@tanstack/react-query'
import { intersectionWith, isEqual, uniq } from 'lodash'
import { useCallback } from 'react'
import { SegmentEvents, useTrackAction } from '@opallabs/analytics'
import {
  globalQueryKeys,
  useDeleteMomentScheduleMutation,
  useCreateBulkMomentsToBoardRequest,
} from '@opallabs/api'
import { boardObjectsIdentifiers, readBoardObjects } from '@opallabs/api/v3'
import { MultiSelectDiffObject, useWorkspace } from '@opallabs/hooks'
import { toast } from '@opallabs/lapidary'
import {
  BULK_SYNCHRONOUS_LIMIT,
  StartAsyncBulkUpdateToast,
  StartSyncBulkUpdateToast,
  defaultBulkToastStartOptions,
} from '../../BulkUpdate/BulkUpdateToasts'
import { handleUnscheduleToast } from '../AddToBoardToasts'
import {
  BoardOption,
  MomentData,
  OnAddToBoardArgs,
  AddToBoardSuccessCallback,
  SelectionMeta,
  UnscheduleMeta,
} from '../types'

const getStoryId = ({
  addedBoards,
  moment,
}: {
  addedBoards: BoardOption[]
  moment: MomentData
}) => {
  if (moment.primaryStoryId) return

  // If adding to multiple boards with different default stories, we use the first one.
  // This is intentional behavior to avoid complexity in story assignment logic.
  const boardWithDefaultStory = addedBoards.find((boardOption) =>
    Boolean(boardOption.meta.defaultStoryId)
  )

  return boardWithDefaultStory?.meta.defaultStoryId
}

const sendTrackEvents = ({
  selectedBoards,
  track,
}: Pick<OnAddToBoardArgs, 'selectedBoards'> & {
  track: ReturnType<typeof useTrackAction>
}) => {
  if (selectedBoards?.added.length) {
    track(SegmentEvents.MomentAddedToBoard)
  }
  if (selectedBoards?.removed.length) {
    track(SegmentEvents.MomentRemovedFromBoard)
  }
}

// Board objects are organized by board in `boardObjectsByBoard`
// so we'll collect all board objects from any deselected board
// for each moment
const collectBoardObjectsToRemove = (
  moments: MomentData[],
  selectedBoards: MultiSelectDiffObject<BoardOption>
): Record<string, string[]> =>
  moments.reduce<Record<string, string[]>>((accum, moment) => {
    const momentBoardObjectIds: string[] = []

    selectedBoards.removed.forEach(({ value: boardId }) => {
      if (moment.boardObjectsByBoard[boardId]) {
        momentBoardObjectIds.push(moment.boardObjectsByBoard[boardId].id)
      }
    })

    accum[moment.id] = momentBoardObjectIds
    return accum
  }, {})

export const useOnAddToBoard = ({
  onSuccess,
}: {
  onSuccess?: AddToBoardSuccessCallback
}) => {
  const { uuid: workspaceId } = useWorkspace()
  const queryClient = useQueryClient()
  const track = useTrackAction()

  // The bulk update request helps coordinate board object and moment
  // update requests so we'll still use it even if we only have
  // a single moment
  const createBulkUpdate = useCreateBulkMomentsToBoardRequest<SelectionMeta>({
    onMutate: ({ moments, selectionMeta }) => {
      const invalidCount = selectionMeta?.invalidIdCount ?? 0
      // Send toast if multiple moments are selected, even if only one is valid
      if (moments.length + invalidCount > 1) {
        const toastId = toast(
          (props) =>
            moments.length > BULK_SYNCHRONOUS_LIMIT ? (
              <StartAsyncBulkUpdateToast {...props} />
            ) : (
              <StartSyncBulkUpdateToast {...props} />
            ),
          defaultBulkToastStartOptions
        )

        return { toastId }
      }
    },
    onSuccess: (response, { moments, selectionMeta }, context) => {
      // If we don't have a response, the bulk operation is being
      // processed async and we should not send any additional toasts
      // or try to refetch data
      if (!response) return

      // Invalidate inbox collections of boards the moments were added to
      // Each moment has the same list of board ids, so just use the first
      moments[0].board_ids.forEach((boardId) =>
        readBoardObjects.invalidateCache(
          queryClient,
          boardObjectsIdentifiers.collection({
            workspaceUuid: workspaceId,
            boardId,
            collectionId: null,
          })
        )
      )
      // Invalidate board collections that moments were removed from
      if (selectionMeta?.moments && selectionMeta?.boards.removed.length > 0) {
        const boardCollections = selectionMeta.moments.flatMap((moment) => {
          const boards: { boardId: string; collectionId: string | null }[] = []
          selectionMeta.boards.removed.forEach(({ value: boardId }) => {
            const { collectionId } = moment.boardObjectsByBoard[boardId]
            if (collectionId !== undefined) {
              boards.push({ boardId, collectionId })
            }
          })

          return boards
        })

        uniq(boardCollections).forEach(({ boardId, collectionId }) => {
          readBoardObjects.invalidateCache(
            queryClient,
            boardObjectsIdentifiers.collection({
              workspaceUuid: workspaceId,
              boardId,
              collectionId,
            })
          )
        })
      }
      // Invalidate moment data queries for the modal
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => {
          // Filter to only moments by workspace queries first (helpful for debugging)
          const keyPrefix = globalQueryKeys.momentsByWorkspace(workspaceId)
          if (!isEqual(queryKey.slice(0, keyPrefix.length), keyPrefix)) {
            return false
          }

          const queryParams =
            queryKey[2] != null
              ? (queryKey[2] as { filter?: { id?: string[] } })
              : undefined
          const ids = Array.isArray(queryParams?.filter?.id)
            ? queryParams.filter.id
            : []

          // Invalidate moments queries that include affected moments
          return intersectionWith(ids, moments, (a, b) => a === b.id).length > 0
        },
      })
      moments.forEach((moment) =>
        queryClient.invalidateQueries(globalQueryKeys.momentById(moment.id))
      )

      // Dismiss sync start bulk update toast
      toast.dismiss(context?.toastId)
      onSuccess?.(response.data, selectionMeta)
    },
  })
  const deleteMomentSchedule = useDeleteMomentScheduleMutation<UnscheduleMeta>({
    onSuccess: (_data, { momentId, meta }) => {
      queryClient.invalidateQueries(globalQueryKeys.momentById(momentId))

      if (meta?.momentTitle) {
        handleUnscheduleToast(meta.momentTitle)
      }
    },
  })

  const onAddToBoard = useCallback(
    ({
      boardSelectionChanged,
      invalidIdCount,
      moments,
      onCloseModal,
      selectedBoards,
      unschedule,
    }: OnAddToBoardArgs) => {
      const selectionChanged =
        selectedBoards !== null && boardSelectionChanged()
      const writes: Promise<unknown>[] = []

      // Unscheduling is only allowed in modal that update a sinlge resource
      if (unschedule && moments.length === 1 && moments[0].scheduleId) {
        const unscheduling = deleteMomentSchedule.mutateAsync({
          momentId: moments[0].id,
          scheduleId: moments[0].scheduleId,
          meta: {
            momentTitle: moments[0].title,
          },
        })
        writes.push(unscheduling)
      }

      if (selectionChanged) {
        const addedBoardIds = selectedBoards.added.map(({ value }) => value)
        const boardObjectsToDelete = selectedBoards.removed.length
          ? collectBoardObjectsToRemove(moments, selectedBoards)
          : undefined

        const updating = createBulkUpdate.mutateAsync({
          moments: moments.map((moment) => {
            const storyId = getStoryId({
              addedBoards: selectedBoards.added,
              moment,
            })
            return {
              id: moment.id,
              board_ids: addedBoardIds,
              delete_board_object_ids: boardObjectsToDelete?.[moment.id] ?? [],
              ...(storyId && { story_id: storyId }),
            }
          }),
          selectionMeta: {
            moments,
            invalidIdCount,
            boards: selectedBoards,
          },
        })
        writes.push(updating)
      }

      sendTrackEvents({ selectedBoards, track })

      return onCloseModal({
        boards: selectedBoards,
        saved: Promise.allSettled(writes),
        unschedule,
      })
    },
    [createBulkUpdate, deleteMomentSchedule, track]
  )

  return onAddToBoard
}
